import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Divider,
  FormControl,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
  alpha,
  Chip,
  Drawer,
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  AttachFile as FilesIcon,
  CalendarMonth as CalendarIcon,
  Dashboard as DashboardIcon,
  Event as AppointmentsIcon,
  Lock as LockIcon,
  NotificationsActive as NotificationsIcon,
  Person as ProfileIcon,
  QuestionAnswer as QuestionnairesIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { ClientDetails, ClientAccessPermission, ClientGroup } from '../../types/client';
import { useClientOptions } from '../../hooks/useClientOptions';
import { UserAvatar } from '../common/UserAvatar.tsx';
import { ProChip } from '../common/ProChip';

// ─── Access control ───────────────────────────────────────────────────────────

const TAB_REQUIRED_MODULES: Record<number, string[]> = {
  0: [],
  1: ['profile'],
  2: ['calendar_liquid', 'calendar_food', 'calendar_workout', 'calendar_medications', 'calendar_feelings', 'calendar_tasks', 'calendar_sleep'],
  5: ['questionnaires'],
  6: ['files'],
  8: [],
  9: [],
  10: [],
  11: [],
};

export const hasTabAccess = (tabIndex: number, permissions: ClientAccessPermission[]): boolean => {
  const required = TAB_REQUIRED_MODULES[tabIndex];
  if (!required || required.length === 0) return true;
  return required.some(moduleCode =>
    permissions.some(p => p.moduleCode === moduleCode && p.permissions.includes('read'))
  );
};

// ─── Tab config ───────────────────────────────────────────────────────────────

const TABS = [
  { index: 0,  labelKey: 'clientProfile.tab.dashboard',     path: 'dashboard',      icon: <DashboardIcon fontSize="small" /> },
  { index: 1,  labelKey: 'clientProfile.tab.profile',       path: 'profile',        icon: <ProfileIcon fontSize="small" /> },
  { index: 2,  labelKey: 'clientProfile.tab.calendar',      path: 'calendar',       icon: <CalendarIcon fontSize="small" /> },
  { index: 5,  labelKey: 'clientProfile.tab.questionnaires', path: 'questionnaires', icon: <QuestionnairesIcon fontSize="small" /> },
  { index: 6,  labelKey: 'clientProfile.tab.files',         path: 'files',          icon: <FilesIcon fontSize="small" /> },
  { index: 8,  labelKey: 'clientProfile.tab.reports',       path: 'reports',        icon: <ReportsIcon fontSize="small" /> },
  { index: 9,  labelKey: 'clientProfile.tab.appointments',  path: 'appointments',   icon: <AppointmentsIcon fontSize="small" /> },
  { index: 10, labelKey: 'clientProfile.tab.notifications', path: 'notifications',  icon: <NotificationsIcon fontSize="small" /> },
  { index: 11, labelKey: 'clientProfile.tab.mailings',      path: 'mailings',       icon: <SendIcon fontSize="small" />, requiresPro: true },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface ClientProfileSidebarProps {
  clientId: number;
  activeTab: number;
  onTabChange: (index: number) => void;
  clientGeneralInfo: ClientDetails['general'];
  accessPermissions: ClientAccessPermission[];
  groups?: ClientGroup[];
  fullAccess?: boolean;
  /** Badge counts per tab index — will be shown as red dot */
  badges?: Partial<Record<number, number>>;
  isMobileOpen?: boolean;
  onClose?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const ClientProfileSidebar: React.FC<ClientProfileSidebarProps> = ({
  clientId,
  activeTab,
  onTabChange,
  clientGeneralInfo,
  accessPermissions,
  groups = [],
  fullAccess = false,
  badges = {},
  isMobileOpen = false,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { connectedClients } = useClientOptions();

  const activeClients = connectedClients.filter(c => c.connectionStatus === 'active');
  
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const currentTabName = pathSegments.length >= 3 ? pathSegments[2] : '';

  const handleClientSwitch = (e: SelectChangeEvent<number>) => {
    const newId = Number(e.target.value);
    if (newId !== clientId) {
      const targetPath = currentTabName ? `/client/${newId}/${currentTabName}` : `/client/${newId}`;
      navigate(`${targetPath}${location.search}`);
      if (onClose) onClose();
    }
  };

  const clientName = `${clientGeneralInfo.name || ''} ${clientGeneralInfo.surname || ''}`.trim();

  const sidebarContent = (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        overflowY: 'auto',
      }}
    >
      {/* Client identity block - Lighter & Consistent */}
      <Box sx={{ px: 2, pt: 2, pb: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Avatar
          src={clientGeneralInfo.avatar || undefined}
          sx={{
            width: 40,
            height: 40,
            fontSize: '1rem',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
          }}
        >
          {clientGeneralInfo.name?.charAt(0).toUpperCase()}
        </Avatar>

        {activeClients.length > 1 ? (
          <FormControl size="small" fullWidth>
            <Select<number>
              value={clientId}
              onChange={handleClientSwitch}
              renderValue={(id) => {
                const c = activeClients.find(cl => cl.id === id);
                return c ? `${c.name}` : ''; // Only name for compactness
              }}
              sx={{ 
                fontSize: '0.8rem', 
                height: 32,
                '& .MuiSelect-select': { py: 0.5 }
              }}
            >
              {activeClients.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <UserAvatar
                      path={c.avatar}
                      name={c.name}
                      sx={{ width: 24, height: 22, fontSize: '0.65rem' }}
                    />
                    <Typography variant="body2" noWrap sx={{ fontSize: '0.85rem' }}>
                      {`${c.name} ${c.surname || ''}`}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Typography
            variant="body2"
            fontWeight={700}
            align="center"
            noWrap
            sx={{ fontSize: '0.85rem', color: 'text.primary' }}
          >
            {clientName}
          </Typography>
        )}

        {groups.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
            {groups.map(g => (
              <Chip
                key={g.id}
                label={g.name}
                size="small"
                sx={{
                  bgcolor: g.color,
                  color: '#fff',
                  fontSize: '8px',
                  height: '16px',
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Divider />

      {/* Navigation - Identical to Main Sidebar Item Style */}
      <List disablePadding sx={{ py: 0.5 }}>
        {TABS.map((tab) => {
          const accessible = fullAccess || hasTabAccess(tab.index, accessPermissions);
          const badgeCount = badges[tab.index] ?? 0;
          const isActive = activeTab === tab.index;

          return (
            <Tooltip
              key={tab.index}
              title={accessible ? '' : t('clientProfile.noAccess')}
              placement="left"
              disableHoverListener={accessible}
            >
              <span>
                <ListItemButton
                  selected={isActive}
                  onClick={() => {
                    if (accessible) {
                      onTabChange(tab.index);
                      if (onClose) onClose();
                    }
                  }}
                  disabled={!accessible}
                  sx={{
                    minHeight: 40,
                    px: 2,
                    borderRadius: 0, // Traditional sidebar look
                    opacity: accessible ? 1 : 0.45,
                    '&.Mui-selected': {
                      bgcolor: alpha('#1976d2', 0.08),
                      color: 'primary.main',
                      '& .MuiListItemIcon-root': { color: 'primary.main' },
                      borderRight: '3px solid',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 1.5,
                      color: isActive ? 'primary.main' : 'text.secondary',
                    }}
                  >
                    {React.cloneElement(tab.icon as React.ReactElement, { fontSize: 'small' })}
                  </ListItemIcon>

                  <ListItemText
                    primary={t(tab.labelKey)}
                    primaryTypographyProps={{
                      fontSize: '0.85rem',
                      noWrap: true,
                      fontWeight: isActive ? 600 : 400
                    }}
                  />

                  {(tab as any).requiresPro && <Box sx={{ ml: 0.5, flexShrink: 0 }}><ProChip /></Box>}

                  {badgeCount > 0 && (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        lineHeight: 1,
                        px: '6px',
                        py: '2px',
                        borderRadius: '10px',
                        bgcolor: isActive ? 'primary.contrastText' : alpha('#1976d2', 0.1),
                        color: isActive ? 'primary.main' : 'primary.main',
                        ml: 1,
                        flexShrink: 0,
                      }}
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </Typography>
                  )}

                  {!accessible && (
                    <LockIcon sx={{ fontSize: 12, ml: 0.5, color: 'text.disabled' }} />
                  )}
                </ListItemButton>
              </span>
            </Tooltip>
          );
        })}
      </List>
    </Box>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={isMobileOpen}
        onClose={onClose}
        sx={{
          display: { xs: 'block', md: 'none' },
          zIndex: (theme) => theme.zIndex.drawer + 2,
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 200 },
        }}
      >
        {sidebarContent}
      </Drawer>

      <Box
        sx={{
          width: 200, // Unified with main sidebar
          position: 'fixed',
          top: '52px',
          right: 0,
          height: 'calc(100vh - 52px)',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          bgcolor: 'background.paper',
          borderLeft: '1px solid',
          borderColor: 'divider',
          overflowY: 'auto',
          zIndex: (theme) => theme.zIndex.appBar - 1,
        }}
      >
        {sidebarContent}
      </Box>
    </>
  );
};
